import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  AthleteView,
  PageResponse,
  RosterEntryView,
  SeasonView,
  SupportedLocale,
  TeamView,
  TrainingGroupView,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { RemoteSelect } from './RemoteSelect';
import { toggled, type AudienceSelection } from './scheduling';
import { structurePath } from './teams';

export interface AudiencePickerProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  departmentId: string;
  selection: AudienceSelection;
  onChange: (selection: AudienceSelection) => void;
  /** Names of athletes already chosen, keyed by athlete id, for an existing event. */
  knownNames?: Readonly<Record<string, string>>;
}

type RosterCollection = 'teams' | 'training-groups';

interface RosterPickerProps {
  locale: SupportedLocale;
  base: string;
  collection: RosterCollection;
  containerId: string;
  athleteIds: readonly string[];
  onToggle: (athleteId: string, name: string) => void;
  onNames: (names: Record<string, string>) => void;
}

/** The current members of one team or group, each with a tick box that invites only that person. */
function RosterPicker({
  locale,
  base,
  collection,
  containerId,
  athleteIds,
  onToggle,
  onNames,
}: RosterPickerProps): React.JSX.Element {
  const [roster, setRoster] = useState<RosterEntryView[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiRequest<RosterEntryView[]>(`${base}/${collection}/${containerId}/members`).then(
      (loaded) => {
        if (cancelled) return;
        setRoster(loaded);
        onNames(
          Object.fromEntries(
            loaded.map((entry) => [entry.athleteId, athleteDisplayName(entry, locale)]),
          ),
        );
      },
      () => {
        if (!cancelled) setRoster([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [base, collection, containerId, locale, onNames]);

  if (roster === null) {
    return <Typography variant="body2">{translate(locale, 'calendar.loading')}</Typography>;
  }
  if (roster.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate(locale, 'calendar.audience.rosterEmpty')}
      </Typography>
    );
  }
  return (
    <FormGroup row sx={{ pl: 2 }}>
      {roster.map((entry) => {
        const name = athleteDisplayName(entry, locale);
        return (
          <FormControlLabel
            key={entry.id}
            label={entry.shirtNumber === null ? name : `${name} #${String(entry.shirtNumber)}`}
            control={
              <Checkbox
                checked={athleteIds.includes(entry.athleteId)}
                onChange={() => {
                  onToggle(entry.athleteId, name);
                }}
              />
            }
          />
        );
      })}
    </FormGroup>
  );
}

/**
 * Who is invited: whole teams and training groups, players ticked one by one from any team's or
 * group's roster, and anyone else found by name. The server deduplicates the union.
 */
export function AudiencePicker({
  locale,
  base,
  departmentId,
  selection,
  onChange,
  knownNames = {},
}: AudiencePickerProps): React.JSX.Element {
  const [seasons, setSeasons] = useState<SeasonView[]>([]);
  const [seasonId, setSeasonId] = useState('');
  const [teams, setTeams] = useState<TeamView[]>([]);
  const [groups, setGroups] = useState<TrainingGroupView[]>([]);
  const [names, setNames] = useState<Record<string, string>>({ ...knownNames });
  const [openRosters, setOpenRosters] = useState<string[]>([]);
  const [pickerKey, setPickerKey] = useState(0);

  const rememberNames = useCallback((found: Record<string, string>) => {
    setNames((current) => ({ ...current, ...found }));
  }, []);

  // Seasons of the department, the active one first.
  useEffect(() => {
    if (departmentId === '') return;
    let cancelled = false;
    apiRequest<SeasonView[]>(structurePath(base, 'seasons')).then(
      (loaded) => {
        if (cancelled) return;
        const open = loaded.filter(
          (season) => season.departmentId === departmentId && season.status !== 'closed',
        );
        setSeasons(open);
        setSeasonId((open.find((season) => season.status === 'active') ?? open[0])?.id ?? '');
      },
      () => {
        if (!cancelled) setSeasons([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [base, departmentId]);

  useEffect(() => {
    if (seasonId === '') return;
    let cancelled = false;
    Promise.all([
      apiRequest<TeamView[]>(structurePath(base, 'teams', seasonId)),
      apiRequest<TrainingGroupView[]>(structurePath(base, 'training-groups', seasonId)),
    ]).then(
      ([loadedTeams, loadedGroups]) => {
        if (cancelled) return;
        setTeams(loadedTeams.filter((team) => team.status !== 'archived'));
        setGroups(loadedGroups.filter((group) => group.status !== 'archived'));
      },
      () => {
        if (cancelled) return;
        setTeams([]);
        setGroups([]);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [base, seasonId]);

  const findAthletes = useCallback(
    async (text: string) => {
      const page = await apiRequest<PageResponse<AthleteView>>(
        `${base}/athletes?limit=20&q=${encodeURIComponent(text)}`,
      );
      return page.items.map((athlete) => ({
        id: athlete.id,
        label: `${athleteDisplayName(athlete, locale)} (${formatCalendarDate(athlete.dateOfBirth, locale)})`,
      }));
    },
    [base, locale],
  );

  const toggleAthlete = (athleteId: string, name: string): void => {
    rememberNames({ [athleteId]: name });
    onChange({ ...selection, athleteIds: toggled(selection.athleteIds, athleteId) });
  };

  const containerRows = (
    collection: RosterCollection,
    containers: readonly { id: string; name: string }[],
    chosen: readonly string[],
    choose: (identifiers: string[]) => void,
  ): React.JSX.Element[] =>
    containers.map((container) => {
      const whole = chosen.includes(container.id);
      const rosterOpen = openRosters.includes(container.id);
      return (
        <Box key={container.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <FormControlLabel
              label={container.name}
              control={
                <Checkbox
                  checked={whole}
                  onChange={() => {
                    choose(toggled(chosen, container.id));
                  }}
                />
              }
            />
            {!whole && (
              <Button
                size="small"
                aria-expanded={rosterOpen}
                onClick={() => {
                  setOpenRosters((current) => toggled(current, container.id));
                }}
              >
                {translate(
                  locale,
                  rosterOpen ? 'calendar.audience.hideRoster' : 'calendar.audience.pickFromRoster',
                )}
              </Button>
            )}
          </Box>
          {!whole && rosterOpen && (
            <RosterPicker
              locale={locale}
              base={base}
              collection={collection}
              containerId={container.id}
              athleteIds={selection.athleteIds}
              onToggle={toggleAthlete}
              onNames={rememberNames}
            />
          )}
        </Box>
      );
    });

  return (
    <Box
      component="fieldset"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, padding: 1.5, margin: 0 }}
    >
      <Typography component="legend" variant="subtitle2" sx={{ px: 0.5 }}>
        {translate(locale, 'calendar.audience.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {translate(locale, 'calendar.audience.hint')}
      </Typography>
      {seasons.length > 1 && (
        <TextField
          select
          size="small"
          sx={{ minWidth: 200, mb: 1 }}
          label={translate(locale, 'calendar.audience.season')}
          value={seasonId}
          onChange={(event) => {
            setSeasonId(event.target.value);
            setOpenRosters([]);
          }}
        >
          {seasons.map((season) => (
            <MenuItem key={season.id} value={season.id}>
              {season.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {teams.length === 0 && groups.length === 0 && (
        <Typography color="text.secondary">
          {translate(locale, 'calendar.audience.none')}
        </Typography>
      )}
      {teams.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {translate(locale, 'calendar.audience.teams')}
          </Typography>
          {containerRows('teams', teams, selection.teamIds, (teamIds) => {
            onChange({ ...selection, teamIds });
          })}
        </>
      )}
      {groups.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
            {translate(locale, 'calendar.audience.groups')}
          </Typography>
          {containerRows('training-groups', groups, selection.groupIds, (groupIds) => {
            onChange({ ...selection, groupIds });
          })}
        </>
      )}
      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <RemoteSelect
          key={pickerKey}
          required={false}
          label={translate(locale, 'calendar.audience.athlete')}
          noMatchesText={translate(locale, 'calendar.audience.noMatches')}
          load={findAthletes}
          onSelect={(identifier, label) => {
            if (identifier === '' || selection.athleteIds.includes(identifier)) return;
            rememberNames({ [identifier]: label });
            onChange({ ...selection, athleteIds: [...selection.athleteIds, identifier] });
            setPickerKey((current) => current + 1);
          }}
        />
      </Box>
      {selection.athleteIds.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
            {translate(locale, 'calendar.audience.picked')} ({selection.athleteIds.length})
          </Typography>
          <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {selection.athleteIds.map((athleteId) => (
              <Chip
                key={athleteId}
                label={names[athleteId] ?? athleteId.slice(0, 8)}
                onDelete={() => {
                  onChange({
                    ...selection,
                    athleteIds: selection.athleteIds.filter((entry) => entry !== athleteId),
                  });
                }}
                deleteIcon={
                  <span aria-label={translate(locale, 'calendar.audience.remove')}>×</span>
                }
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
